import { useId } from 'react';
import {
    SLIDE_LEVEL_NONE_CATEGORY_ID,
    SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID,
    DEFAULT_NONE_CATEGORY_LABEL,
} from './slideLevelClassificationModel';
import {
    truncateWithEllipsis,
    compactFreeTextOptionHeadline,
} from './HybridClassSelect';
import styles from './ChoiceList.module.css';

/**
 * Checkbox list for choose-all-that-apply (QuestionsPanel).
 * Order: categories, optional Other, None last. None is exclusive with other rows.
 * Other: first check opens editor; click label while checked opens editor; checkbox mark toggles off.
 */
export function MultiChoiceList({
    categories = [],
    selectedIds = [],
    freeTextAnswer = '',
    disabled = false,
    labelledById,
    onToggleCategory,
    onActivateOther,
}) {
    const listId = useId();
    const selected = new Set(
        (Array.isArray(selectedIds) ? selectedIds : []).map((id) => String(id)),
    );

    const freePreviewRaw = String(freeTextAnswer ?? '').trim();
    const freePreview = freePreviewRaw
        ? truncateWithEllipsis(freePreviewRaw, 40)
        : null;

    return (
        <div className={styles.listPanel}>
            <ul
                id={listId}
                className={styles.listVertical}
                role="listbox"
                aria-multiselectable="true"
                aria-labelledby={labelledById}
            >
                {categories.map((c) => {
                    const id = String(c.id);
                    const isNone = id === SLIDE_LEVEL_NONE_CATEGORY_ID;
                    const isOther = id === SLIDE_LEVEL_FREE_TEXT_CATEGORY_ID;
                    const label = c.label?.trim()
                        ? c.label
                        : (isNone ? DEFAULT_NONE_CATEGORY_LABEL : id);
                    const isSelected = selected.has(id);
                    const freeRowHeadline = isOther
                        ? compactFreeTextOptionHeadline(label)
                        : '';
                    const freeTextAriaLabel = isOther
                        ? (freePreviewRaw
                            ? `${label}. Current answer: ${truncateWithEllipsis(freePreviewRaw, 72)}. Opens text editor.`
                            : `${label}. Opens text editor.`)
                        : undefined;

                    function toggle() {
                        if (!disabled) onToggleCategory(id, label);
                    }

                    function activateOtherOrToggle() {
                        if (disabled) return;
                        if (isOther && isSelected) {
                            onActivateOther?.();
                            return;
                        }
                        toggle();
                    }

                    return (
                        <li key={c.id} role="presentation">
                            <button
                                type="button"
                                role="option"
                                aria-selected={isSelected}
                                disabled={disabled}
                                aria-label={freeTextAriaLabel}
                                className={[
                                    styles.option,
                                    isSelected ? styles.optionSelected : '',
                                    isOther ? styles.freeTextOption : '',
                                    isNone ? styles.noneOption : '',
                                ].filter(Boolean).join(' ')}
                                onClick={activateOtherOrToggle}
                            >
                                <span
                                    className={[
                                        styles.checkboxMark,
                                        isSelected ? styles.checkboxMarkChecked : '',
                                    ].filter(Boolean).join(' ')}
                                    aria-hidden
                                    onClick={(e) => {
                                        if (!isOther) return;
                                        e.stopPropagation();
                                        toggle();
                                    }}
                                >
                                    {isSelected ? '✓' : ''}
                                </span>
                                {isOther ? (
                                    <span className={styles.optionLabel}>
                                        {freePreview
                                            ? `${freeRowHeadline}: ${freePreview}`
                                            : label}
                                    </span>
                                ) : (
                                    <span className={styles.optionLabel}>{label}</span>
                                )}
                            </button>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}
